import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  FileSpreadsheet,
  FileText,
  Loader2,
  RotateCw,
  Ticket as TicketIcon,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import type { EventReportPayload, MemberRole } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QueryError } from "@/components/query-error";
import { useAuthenticatedMe } from "@/features/organizers/api";
import {
  downloadEventReport,
  useEventReportDetailQuery,
  useRegenerateEventReportMutation,
} from "@/features/reports/api";
import { showApiError } from "@/lib/api-client";
import { usePaymentProviderLabel, useRefundReasonLabel } from "@/lib/enum-labels";
import { ISTANBUL_TZ, useFormat, type Format } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const REPORT_DATE_TIME: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ISTANBUL_TZ,
};
const REPORT_TIME: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ISTANBUL_TZ,
};
const HOUR_MS = 60 * 60 * 1000;

/**
 * Etkinlik Sonu Raporu — detay sayfası.
 *
 * Snapshot payload'ından tüm KPI/tablo'ları render eder. Raporu gören herkes
 * Excel/PDF indirebilir; Owner için "Raporu Yeniden Üret" butonu da görünür
 * (eski snapshot silinir, yenisi üretilir, audit log'a kayıt düşer).
 */
export function EventReportDetailPage() {
  return (
    <DashboardPage>
      <EventReportDetailView />
    </DashboardPage>
  );
}

function EventReportDetailView() {
  const params = useParams<{ eventId: string }>();
  const eventId = params.eventId ?? null;
  const me = useAuthenticatedMe();
  const query = useEventReportDetailQuery(eventId);
  const t = useT("eventReport");

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (query.error) {
    const isNotFound = query.error.status === 404;
    return (
      <div className="space-y-4">
        <PageHeader
          title={isNotFound ? t("notFoundTitle") : t("fallbackTitle")}
          backHref="/reports/events"
          backLabel={t("back")}
        />
        {isNotFound ? (
          <Card>
            <CardContent className="flex items-center gap-3 py-6 text-sm">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <div>
                <div className="font-medium">{t("notGenerated")}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {t("notGeneratedHint")}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <QueryError
            message={t("loadError")}
            detail={query.error.detail}
            onRetry={() => query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </div>
    );
  }

  if (!query.data) return null;

  const role: MemberRole | undefined = me.data?.role;
  const canRegenerate = role === "Owner";

  return (
    <ReportDetailContent
      eventId={query.data.eventId}
      title={query.data.eventTitle}
      eventEndsAt={query.data.eventEndsAt}
      snapshotTakenAt={query.data.snapshotTakenAt}
      payload={query.data.payload}
      canRegenerate={canRegenerate}
    />
  );
}

function ReportDetailContent({
  eventId,
  title,
  eventEndsAt,
  snapshotTakenAt,
  payload,
  canRegenerate,
}: {
  eventId: string;
  title: string;
  eventEndsAt: string;
  snapshotTakenAt: string;
  payload: EventReportPayload;
  canRegenerate: boolean;
}) {
  const regenerate = useRegenerateEventReportMutation();
  const t = useT("eventReport");
  const fmt = useFormat();
  const [confirming, setConfirming] = useState(false);
  const [exporting, setExporting] = useState<"xlsx" | "pdf" | null>(null);

  async function handleExport(format: "xlsx" | "pdf") {
    setExporting(format);
    try {
      await downloadEventReport(eventId, format, fmt.locale);
    } catch (err) {
      showApiError(err, t("downloadFailed"));
    } finally {
      setExporting(null);
    }
  }

  async function handleRegenerate() {
    try {
      await regenerate.mutateAsync(eventId);
      toast.success(t("regenerated"));
      setConfirming(false);
    } catch (err) {
      showApiError(err, t("regenerateFailed"));
    }
  }

  const { summary, ticketTypes, paymentBreakdown, refundAnalysis, promotionUsage, salesTrend, checkIn } =
    payload;
  const currency = payload.currency;

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={t("meta", {
          ended: fmt.dateTime(eventEndsAt, REPORT_DATE_TIME),
          taken: fmt.dateTime(snapshotTakenAt, REPORT_DATE_TIME),
        })}
        backHref="/reports/events"
        backLabel={t("back")}
      >
        {canRegenerate && confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t("regenerateWarning")}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
              disabled={regenerate.isPending}
            >
              {t("dismiss")}
            </Button>
            <Button
              size="sm"
              onClick={handleRegenerate}
              disabled={regenerate.isPending}
            >
              {regenerate.isPending ? (
                <>
                  <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
                  {t("regenerating")}
                </>
              ) : (
                t("confirm")
              )}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("xlsx")}
              disabled={exporting !== null}
            >
              {exporting === "xlsx" ? (
                <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileSpreadsheet className="me-1.5 h-3.5 w-3.5" />
              )}
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("pdf")}
              disabled={exporting !== null}
            >
              {exporting === "pdf" ? (
                <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileText className="me-1.5 h-3.5 w-3.5" />
              )}
              PDF
            </Button>
            {canRegenerate ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirming(true)}
              >
                <RotateCw className="me-1.5 h-3.5 w-3.5" />
                {t("regenerate")}
              </Button>
            ) : null}
          </div>
        )}
      </PageHeader>

      <SummaryCards summary={summary} currency={currency} />
      <TicketTypeTable rows={ticketTypes} currency={currency} />
      <div className="grid gap-4 lg:grid-cols-2">
        <PaymentBreakdownCard rows={paymentBreakdown} currency={currency} />
        <RefundAnalysisCard analysis={refundAnalysis} currency={currency} />
      </div>
      {promotionUsage.length > 0 ? (
        <PromotionsCard rows={promotionUsage} currency={currency} />
      ) : null}
      {salesTrend.length > 0 ? (
        <SalesTrendCard points={salesTrend} currency={currency} />
      ) : null}
      <CheckInCard metrics={checkIn} />
    </div>
  );
}

/* ────────────────────────── Summary cards ────────────────────────── */

function SummaryCards({
  summary,
  currency,
}: {
  summary: EventReportPayload["summary"];
  currency: EventReportPayload["currency"];
}) {
  const t = useT("eventReport.summary");
  const fmt = useFormat();
  const money = (value: number) => fmt.money(value, currency);

  const cards: Array<{
    label: string;
    value: string;
    hint?: string;
    icon: typeof Wallet;
    tone?: "primary" | "default";
  }> = [
    {
      label: t("gross"),
      value: money(summary.grossRevenue),
      hint: t("grossHint", {
        count: summary.orderCount,
        orders: fmt.number(summary.orderCount),
        average: money(summary.averageOrderValue),
      }),
      icon: Wallet,
      tone: "primary",
    },
    {
      label: t("net"),
      value: money(summary.organizerNetRevenue),
      hint: t("netHint", { value: money(summary.commissionTotal) }),
      icon: TrendingUp,
    },
    {
      label: t("sold"),
      value: fmt.number(summary.ticketsSold),
      hint: t("soldHint", { value: money(summary.averageTicketPrice) }),
      icon: TicketIcon,
    },
    {
      label: t("attendance"),
      value: `${fmt.number(summary.ticketsCheckedIn)} / ${fmt.number(summary.ticketsSold)}`,
      hint: t("attendanceHint", {
        count: summary.ticketsNoShow,
        rate: fmt.percent(summary.attendanceRate * 100),
        noShow: fmt.number(summary.ticketsNoShow),
      }),
      icon: Users,
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card
            key={c.label}
            className={cn(c.tone === "primary" && "border-primary/30")}
          >
            <CardContent className="space-y-1.5 p-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{c.label}</span>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="text-xl font-semibold tabular-nums">
                {c.value}
              </div>
              {c.hint ? (
                <div className="text-xs text-muted-foreground">{c.hint}</div>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/* ───────────────────────── Ticket type table ───────────────────────── */

function TicketTypeTable({
  rows,
  currency,
}: {
  rows: EventReportPayload["ticketTypes"];
  currency: EventReportPayload["currency"];
}) {
  const t = useT("eventReport.ticketTypes");
  const fmt = useFormat();
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <TicketIcon className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-start font-medium">{t("type")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("sold")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("checkedIn")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("gross")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("commission")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("net")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.ticketTypeId}>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5 text-end tabular-nums">
                    {fmt.number(r.sold)}
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums">
                    {fmt.number(r.checkedIn)}
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums">
                    {fmt.money(r.grossRevenue, currency)}
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums text-muted-foreground">
                    {fmt.money(r.commissionTotal, currency)}
                  </td>
                  <td className="px-4 py-2.5 text-end font-medium tabular-nums">
                    {fmt.money(r.organizerNetRevenue, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────────────────── Payment & refund ───────────────────────── */

function PaymentBreakdownCard({
  rows,
  currency,
}: {
  rows: EventReportPayload["paymentBreakdown"];
  currency: EventReportPayload["currency"];
}) {
  const t = useT("eventReport.payments");
  const fmt = useFormat();
  const providerLabel = usePaymentProviderLabel();
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <CreditCard className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li
                key={r.provider}
                className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-medium">{providerLabel(r.provider)}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("orders", { count: r.orderCount, orders: fmt.number(r.orderCount) })}
                  </div>
                </div>
                <div className="font-medium tabular-nums">
                  {fmt.money(r.grossRevenue, currency)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RefundAnalysisCard({
  analysis,
  currency,
}: {
  analysis: EventReportPayload["refundAnalysis"];
  currency: EventReportPayload["currency"];
}) {
  const t = useT("eventReport.refunds");
  const fmt = useFormat();
  const reasonLabel = useRefundReasonLabel();
  const total = analysis.totalRefundedAmount;
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <RotateCw className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Stat label={t("total")} value={fmt.money(total, currency)} />
          <Stat label={t("tickets")} value={fmt.number(analysis.refundedTicketCount)} />
          <Stat label={t("full")} value={fmt.number(analysis.fullRefundCount)} />
          <Stat label={t("partial")} value={fmt.number(analysis.partialRefundCount)} />
        </div>
        {analysis.byReason.length > 0 ? (
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {t("byReason")}
            </div>
            <ul className="space-y-1.5 text-sm">
              {analysis.byReason.map((r) => (
                <li
                  key={r.reason}
                  className="flex items-center justify-between"
                >
                  <span>{reasonLabel(r.reason)}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {t("reasonLine", {
                      count: fmt.number(r.count),
                      amount: fmt.money(r.amount, currency),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/* ───────────────────────── Promotions ───────────────────────── */

function PromotionsCard({
  rows,
  currency,
}: {
  rows: EventReportPayload["promotionUsage"];
  currency: EventReportPayload["currency"];
}) {
  const t = useT("eventReport.promotions");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-start font-medium">{t("name")}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t("code")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("redemptions")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("discount")}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t("freeTickets")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.promotionId}>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {r.code ? (
                      <Badge variant="outline" className="font-mono">
                        {r.code}
                      </Badge>
                    ) : (
                      <span className="text-xs">{t("automatic")}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums">
                    {fmt.number(r.redemptionCount)}
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums">
                    {fmt.money(r.discountTotal, currency)}
                  </td>
                  <td className="px-4 py-2.5 text-end tabular-nums">
                    {r.freeQuantityTotal > 0 ? fmt.number(r.freeQuantityTotal) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────────────────── Sales trend ───────────────────────── */

function SalesTrendCard({
  points,
  currency,
}: {
  points: EventReportPayload["salesTrend"];
  currency: EventReportPayload["currency"];
}) {
  const t = useT("eventReport.trend");
  const fmt = useFormat();
  const max = Math.max(1, ...points.map((p) => p.grossRevenue));
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {points.map((p) => {
            const pct = (p.grossRevenue / max) * 100;
            return (
              <li key={p.date} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{formatDayKey(p.date, fmt)}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {t("line", {
                      count: p.ticketCount,
                      tickets: fmt.number(p.ticketCount),
                      amount: fmt.money(p.grossRevenue, currency),
                    })}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

/* ───────────────────────── Check-in ───────────────────────── */

function CheckInCard({
  metrics,
}: {
  metrics: EventReportPayload["checkIn"];
}) {
  const t = useT("eventReport.checkIn");
  const fmt = useFormat();
  if (!metrics.firstCheckedInAt || metrics.hourlyBuckets.length === 0) {
    return (
      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0">
          <Users className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">{t("title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("none")}</p>
        </CardContent>
      </Card>
    );
  }

  const max = Math.max(1, ...metrics.hourlyBuckets.map((b) => b.count));
  const peak = metrics.hourlyBuckets.reduce((best, b) => (b.count > best.count ? b : best));
  const peakStart = new Date(peak.hourUtc);
  const peakRange = `${fmt.time(peakStart, REPORT_TIME)}–${fmt.time(
    new Date(peakStart.getTime() + HOUR_MS),
    REPORT_TIME,
  )}`;

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <Users className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">{t("distribution")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Stat
            label={t("first")}
            value={fmt.time(metrics.firstCheckedInAt, REPORT_TIME)}
          />
          <Stat
            label={t("last")}
            value={
              metrics.lastCheckedInAt ? fmt.time(metrics.lastCheckedInAt, REPORT_TIME) : "—"
            }
          />
          <Stat
            label={t("peak")}
            value={t("peakValue", { range: peakRange, count: fmt.number(peak.count) })}
          />
        </div>
        <div className="space-y-1.5">
          {metrics.hourlyBuckets.map((b) => {
            const pct = (b.count / max) * 100;
            return (
              <div key={b.hourUtc} className="flex items-center gap-2 text-xs">
                <span className="w-12 text-muted-foreground">
                  {fmt.time(b.hourUtc, REPORT_TIME)}
                </span>
                <div className="h-1.5 flex-1 rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-8 text-end tabular-nums">{fmt.number(b.count)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────────────────── helpers ───────────────────────── */

/** Trend anahtarı saatsiz `yyyy-MM-dd`; UTC'de biçimlenir ki tarayıcı saat dilimi günü kaydırmasın. */
function formatDayKey(key: string, fmt: Format): string {
  const date = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return key;
  return fmt.date(date, { day: "2-digit", month: "short", timeZone: "UTC" });
}
